import {Injectable} from '@angular/core';
import {environment} from '../environments/environment';
import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool
} from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';
import * as CognitoIdentity from 'aws-sdk/clients/cognitoidentity';
import * as STS from 'aws-sdk/clients/sts';
import { HttpHeaders } from '@angular/common/http';

export interface CognitoCallback {
    cognitoCallback(message: string, result: any): void;
}

export interface LoggedInCallback {
    isLoggedIn(message: string, loggedIn: boolean): void;
}

export interface Callback {
    callback(): void;
    callbackWithParam(result: any): void;
}

export interface ResendCodeCallback {
    resendCodeCallback(message: string, result: any): void;
}

export interface AccessTokenCallback {
    accessTokenCallback(result: any): void;
}

export interface RefreshTokenCallback {
    refreshTokenCallback(result: any): void;
}

@Injectable()
export class CognitoUtil {

    public static _REGION = environment.region;

    public static _IDENTITY_POOL_ID = environment.identityPoolId;
    public static _USER_POOL_ID = environment.userPoolId;
    public static _CLIENT_ID = environment.clientId;

    public static _POOL_DATA = {
        UserPoolId: CognitoUtil._USER_POOL_ID,
        ClientId: CognitoUtil._CLIENT_ID
    };

    public cognitoCreds: AWS.CognitoIdentityCredentials;

    constructor() {
        AWS.config.region = environment.region;
    }

    createOptions(refresh: boolean = true) {
        if (refresh) this.refresh();
        return {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('AWSCreds'),
            'Identity': localStorage.getItem('CognitoId'),
            'Sub': localStorage.getItem('CognitoSub')
          })
        }
      }

    getUserPool() {
        return new CognitoUserPool(CognitoUtil._POOL_DATA);
    }

    getCurrentUser() {
        return this.getUserPool().getCurrentUser();
    }

    // AWS Stores Credentials in many ways, and with TypeScript this means that
    // getting the base credentials we authenticated with from the AWS globals gets really murky,
    // having to get around both class extension and unions. Therefore, we're going to give
    // developers direct access to the raw, unadulterated CognitoIdentityCredentials
    // object at all times.
    setCognitoCreds(creds: AWS.CognitoIdentityCredentials) {
        this.cognitoCreds = creds;
    }

    getCognitoCreds() {
        return this.cognitoCreds;
    }

    // This method takes in a raw jwtToken and uses the global AWS config options to build a
    // CognitoIdentityCredentials object and store it for us. It also returns the object to the caller
    // to avoid unnecessary calls to setCognitoCreds.

    buildCognitoCreds(idTokenJwt: string) {
        const url = 'cognito-idp.' + CognitoUtil._REGION.toLowerCase() + '.amazonaws.com/' + CognitoUtil._USER_POOL_ID;
        const logins: CognitoIdentity.LoginsMap = {};
        logins[url] = idTokenJwt;
        const params = {
            IdentityPoolId: CognitoUtil._IDENTITY_POOL_ID, /* required */
            Logins: logins
        };
        const creds = new AWS.CognitoIdentityCredentials(params);
        this.setCognitoCreds(creds);

        return creds;
    }

    async buildCognitoCredsForGoogle(idTokenJwt: string) {
        return new Promise((resolve, reject) => {
            const url = 'cognito-idp.' + CognitoUtil._REGION.toLowerCase() + '.amazonaws.com/' + CognitoUtil._USER_POOL_ID;
            const logins: CognitoIdentity.LoginsMap = {};
            logins[url] = idTokenJwt;

            AWS.config.region = 'us-west-2'
            AWS.config.credentials = new AWS.CognitoIdentityCredentials({
                IdentityPoolId: CognitoUtil._IDENTITY_POOL_ID,
                Logins: logins
            });

            var creds;
            AWS.config.getCredentials(function (err) {
                if (err) {
                    console.log(err.stack);
                }
                else {
                    // successfully loaded creds
                    creds = AWS.config.credentials['params'];
                    const sts = new STS();
                    try {
                        sts.getCallerIdentity(function (err, data) {
                            localStorage.setItem('AWSCreds', JSON.stringify(creds));
                            localStorage.setItem('CognitoId', creds['IdentityId']);
                            resolve();
                        });
                    } catch (e) {
                        console.log(e);
                    }
                }
            })
            if (creds) {
                this.setCognitoCreds(creds);
            }
        });
    }


    getCognitoIdentity(): string {
        return this.cognitoCreds.identityId;
    }

    getAccessToken(callback: AccessTokenCallback): void {
        if (callback == null) {
            throw('CognitoUtil: callback in getAccessToken is null...returning');
        }
        if (this.getCurrentUser() != null) {
            this.getCurrentUser().getSession(function (err, session) {
                if (err) {
                    console.log('CognitoUtil: Can\'t set the credentials:' + err);
                    callback.accessTokenCallback(null);
                } else {
                    if (session.isValid()) {
                        callback.accessTokenCallback(session.getAccessToken().getJwtToken());
                    }
                }
            });
        } else {
            callback.accessTokenCallback(null);
        }
    }

    getIdToken(callback: Callback): void {
        if (callback == null) {
            throw('CognitoUtil: callback in getIdToken is null...returning');
        }
        if (this.getCurrentUser() != null) {
            this.getCurrentUser().getSession(function (err, session) {
                if (err) {
                    console.log('CognitoUtil: Can\'t set the credentials:' + err);
                    callback.callbackWithParam(null);
                } else {
                    if (session.isValid()) {
                        callback.callbackWithParam(session.getIdToken().getJwtToken());
                    } else {
                        console.log('CognitoUtil: Got the id token, but the session isn\'t valid');
                    }
                }
            });
        } else {
            callback.callbackWithParam(null);
        }
    }

    getRefreshToken(callback: RefreshTokenCallback): void {
        if (callback == null) {
            throw('CognitoUtil: callback in getRefreshToken is null...returning');
        }
        if (this.getCurrentUser() != null) {
            this.getCurrentUser().getSession(function (err, session) {
                if (err) {
                    console.log('CognitoUtil: Can\'t set the credentials:' + err);
                    callback.refreshTokenCallback(null);
                } else {
                    if (session.isValid()) {
                        callback.refreshTokenCallback(session.getRefreshToken());
                    }
                }
            });
        } else {
            callback.refreshTokenCallback(null);
        }
    }

    refresh(): void {
        let currentUser = this.getCurrentUser();
        currentUser.getSession(function (err, session) {
            if (err) {
                console.log('CognitoUtil: Can\'t set the credentials:' + err);
            } else {
                if (session.isValid()) {
                    let idToken = session.getIdToken().getJwtToken();
                    const accessToken = session.getAccessToken().getJwtToken();
                    
                    let creds = {};
                    let identityId = localStorage.getItem("aws.cognito.identity-id." + environment.identityPoolId);

                    creds["IdentityPoolId"] = CognitoUtil._IDENTITY_POOL_ID;

                    const url = 'cognito-idp.' + CognitoUtil._REGION.toLowerCase() + '.amazonaws.com/' + CognitoUtil._USER_POOL_ID;

                    let logins = {};
                    logins[url] = idToken;

                    creds["Logins"] = logins;
                    creds["RoleSessionName"] = "web-identity";
                    creds["IdentityId"] = identityId;
                    
                    let credsString = JSON.stringify(creds);

                    localStorage.setItem("AWSCreds", credsString);
                    localStorage.setItem("AccessToken", accessToken);
                    //console.log("Session refreshed");
                } else {
                    console.log('CognitoUtil: refreshed but session is still not valid');
                }
            }
        });
    }
}
